import { useEffect, useState } from "react";
import { CheckCircle2, CreditCard, Loader2, MapPin, Ticket, Wallet, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { FREE_SHIPPING_THRESHOLD, SHIPPING_FEE, createRazorpayOrder, getConfig, getSettings, inr, placeOrder, validateCoupon, verifyPayment, formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const initialForm = { name: "", email: "", phone: "", address: "", city: "", state: "", pincode: "", notes: "" };

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
    <input
      data-testid={testid}
      {...props}
      className="mt-1.5 w-full rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition"
    />
  </label>
);

export const CheckoutDialog = ({ open, onOpenChange, onOrderPlaced }) => {
  const { items, subtotal, clear } = useCart();
  const [form, setForm] = useState(initialForm);
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState(null);
  const [razorpayEnabled, setRazorpayEnabled] = useState(false);
  const [payMethod, setPayMethod] = useState("cod");
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [gstInvoice, setGstInvoice] = useState(false);
  const [gstin, setGstin] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [threshold, setThreshold] = useState(FREE_SHIPPING_THRESHOLD);
  const [fee, setFee] = useState(SHIPPING_FEE);
  const [location, setLocation] = useState(null);
  const [locBusy, setLocBusy] = useState(false);

  useEffect(() => {
    getConfig()
      .then((c) => setRazorpayEnabled(!!c.razorpay_enabled))
      .catch(() => {});
    getSettings()
      .then((s) => {
        setThreshold(s.free_shipping_above);
        setFee(s.shipping_fee);
      })
      .catch(() => {});
    try {
      const saved = JSON.parse(localStorage.getItem("classina_customer") || "null");
      if (saved) setForm((f) => ({ ...f, name: saved.name || f.name, email: saved.email || f.email, phone: saved.phone || f.phone }));
    } catch {
      /* no saved customer */
    }
  }, []);

  const discount = coupon?.discount || 0;
  const shipping = subtotal - discount >= threshold ? 0 : fee;
  const grandTotal = subtotal - discount + shipping;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pinLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location access");
      return;
    }
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Number(pos.coords.latitude.toFixed(6));
        const lng = Number(pos.coords.longitude.toFixed(6));
        setLocation({ lat, lng, maps_url: `https://www.google.com/maps?q=${lat},${lng}` });
        setLocBusy(false);
        toast.success("Location pinned — our delivery partner will find your exact doorstep");
      },
      () => {
        setLocBusy(false);
        toast.error("Couldn't get your location — please allow location access and try again");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponBusy(true);
    try {
      const mrpSubtotal = items.reduce((s, i) => s + (i.mrp || i.price) * i.qty, 0);
      const res = await validateCoupon(couponInput.trim(), subtotal, mrpSubtotal);
      setCoupon(res);
      toast.success(`Coupon ${res.code} applied — you save ${inr(res.discount)}`);
    } catch (err) {
      setCoupon(null);
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCouponBusy(false);
    }
  };

  const finishOrder = (placed, paid) => {
    setOrder({ ...placed, status: paid ? "paid" : placed.status });
    clear();
    setPlacing(false);
    onOrderPlaced?.();
  };

  const startOnlinePayment = async (placed) => {
    try {
      const rp = await createRazorpayOrder(placed.id);
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Could not load payment window");
      const rzp = new window.Razorpay({
        key: rp.key_id,
        amount: rp.amount,
        currency: rp.currency,
        order_id: rp.razorpay_order_id,
        name: "Classina",
        description: `Order ${placed.order_no}`,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#3f5426" },
        handler: async (res) => {
          try {
            await verifyPayment({ order_id: placed.id, ...res });
            toast.success("Payment received — order confirmed");
            finishOrder(placed, true);
          } catch {
            toast.error("Payment verification failed — we'll confirm manually");
            finishOrder(placed, false);
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment not completed — order saved, pay on confirmation");
            finishOrder(placed, false);
          },
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start online payment");
      finishOrder(placed, false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const placed = await placeOrder({
        ...form,
        payment_mode: payMethod,
        coupon: coupon?.code || null,
        gst_invoice: gstInvoice,
        gstin: gstInvoice ? gstin : null,
        business_name: gstInvoice ? businessName : null,
        location,
        items: items.map((i) => ({ slug: i.slug, qty: i.qty, variant: i.variant, pack: i.pack })),
      });
      if (payMethod === "online") {
        await startOnlinePayment(placed);
        return;
      }
      toast.success("Order placed successfully");
      finishOrder(placed, false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPlacing(false);
    }
  };

  const close = (o) => {
    onOpenChange(o);
    if (!o) setTimeout(() => { setOrder(null); setForm(initialForm); setLocation(null); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="checkout-dialog" className="max-w-2xl bg-background max-h-[90vh] overflow-y-auto">
        {order ? (
          <div data-testid="order-confirmation" className="text-center py-8 px-4">
            <CheckCircle2 className="w-14 h-14 text-primary mx-auto" />
            <DialogTitle className="font-serif text-3xl font-semibold mt-5">
              {order.status === "paid" ? "Payment received!" : "Order placed!"}
            </DialogTitle>
            <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-md mx-auto">
              Thank you, {order.customer.name.split(" ")[0]}.{" "}
              {order.status === "paid"
                ? "Your payment is confirmed and a receipt is on its way to your inbox."
                : "Our team will call you within one business day to confirm your order. Pay on confirmation or cash on delivery."}
            </p>
            <div className="mt-6 inline-block bg-secondary/50 rounded-xl px-8 py-5">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Order number</p>
              <p data-testid="order-number" className="font-serif text-2xl font-semibold mt-1">{order.order_no}</p>
              <p className="text-sm text-muted-foreground mt-1">{order.items.reduce((s, i) => s + i.qty, 0)} items · {inr(order.total)}</p>
              <p data-testid="order-payment-status" className={`text-[10px] uppercase tracking-[0.2em] font-bold mt-2 ${order.status === "paid" ? "text-primary" : "text-amber-700"}`}>
                {order.status === "paid" ? "Paid online" : "Pay on confirmation / COD"}
              </p>
            </div>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <a
                href="/track"
                data-testid="order-track-button"
                className="mt-8 rounded-full border border-foreground/20 px-8 py-3 text-sm font-semibold hover:bg-secondary transition-colors inline-block"
              >
                Track this order
              </a>
              <button
                data-testid="order-continue-button"
                onClick={() => close(false)}
                className="mt-8 rounded-full bg-primary text-primary-foreground px-8 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform"
              >
                Continue shopping
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-1">
            <DialogTitle className="font-serif text-2xl md:text-3xl font-semibold tracking-tight">Checkout</DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">Delivery details — we ship across India.</p>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <Field label="Full name" testid="checkout-name" required value={form.name} onChange={set("name")} placeholder="Asha Patil" />
              <Field label="Phone" testid="checkout-phone" required type="tel" pattern="[0-9+ -]{10,15}" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" />
              <Field label="Email" testid="checkout-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
              <Field label="Pincode" testid="checkout-pincode" required pattern="[0-9]{6}" maxLength={6} value={form.pincode} onChange={set("pincode")} placeholder="411002" />
              <div className="sm:col-span-2">
                <Field label="Address" testid="checkout-address" required value={form.address} onChange={set("address")} placeholder="Flat, street, landmark" />
              </div>
              <Field label="City" testid="checkout-city" required value={form.city} onChange={set("city")} placeholder="Mumbai" />
              <Field label="State" testid="checkout-state" required value={form.state} onChange={set("state")} placeholder="Maharashtra" />
            </div>

            <div className="mt-4 rounded-xl border border-dashed border-primary/40 bg-primary/5 p-4" data-testid="checkout-location-box">
              {location ? (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold text-primary flex items-center gap-1.5" data-testid="checkout-location-pinned">
                    <MapPin className="w-4 h-4 shrink-0" />
                    Location pinned —{" "}
                    <a href={location.maps_url} target="_blank" rel="noopener noreferrer" data-testid="checkout-location-link" className="underline underline-offset-2">
                      view on Google Maps
                    </a>
                  </p>
                  <button
                    type="button"
                    data-testid="checkout-location-remove"
                    onClick={() => setLocation(null)}
                    className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                    aria-label="Remove location"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    data-testid="checkout-pin-location"
                    onClick={pinLocation}
                    disabled={locBusy}
                    className="w-full flex items-center justify-center gap-2 text-xs font-bold text-primary py-0.5 hover:opacity-80 transition disabled:opacity-60"
                  >
                    {locBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                    {locBusy ? "Getting your location…" : "Pin my exact delivery location (Google Maps)"}
                  </button>
                  <p className="text-[10px] text-muted-foreground text-center mt-1.5">
                    Optional — helps our delivery partner reach your doorstep faster
                  </p>
                </>
              )}
            </div>

            <label className="block mt-4">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Order notes (optional)</span>
              <textarea
                data-testid="checkout-notes"
                rows={2}
                value={form.notes}
                onChange={set("notes")}
                placeholder="Anything we should know?"
                className="mt-1.5 w-full rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition resize-none"
              />
            </label>

            <div className="mt-4 grid grid-cols-[1fr_auto] gap-2">
              <div className="relative">
                <Ticket className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="coupon-input"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  placeholder="Coupon code"
                  className="w-full rounded-md border border-input bg-white/60 pl-10 pr-3.5 py-2.5 text-sm font-mono uppercase outline-none focus:ring-2 focus:ring-primary transition"
                />
              </div>
              <button
                type="button"
                data-testid="coupon-apply-button"
                onClick={applyCoupon}
                disabled={couponBusy}
                className="rounded-md bg-secondary px-5 py-2.5 text-sm font-bold hover:brightness-95 transition disabled:opacity-60"
              >
                {couponBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
              </button>
            </div>
            {coupon && (
              <p data-testid="coupon-applied" className="mt-2 text-xs font-bold text-primary flex items-center gap-2">
                Coupon {coupon.code} applied — you save {inr(coupon.discount)}
                <button
                  type="button"
                  data-testid="coupon-remove"
                  onClick={() => { setCoupon(null); setCouponInput(""); }}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  aria-label="Remove coupon"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </p>
            )}

            <label className="mt-4 flex items-center gap-2.5 cursor-pointer w-fit" data-testid="gst-invoice-toggle">
              <input type="checkbox" checked={gstInvoice} onChange={(e) => setGstInvoice(e.target.checked)} className="w-4 h-4 accent-[#3f5426]" />
              <span className="text-sm font-semibold">I need a GST invoice (business purchase)</span>
            </label>
            {gstInvoice && (
              <div className="grid sm:grid-cols-2 gap-4 mt-3">
                <Field label="Business name" testid="checkout-business-name" required value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Company / firm name" />
                <Field label="GSTIN" testid="checkout-gstin" required value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} placeholder="27ABCDE1234F1Z5" maxLength={15} />
              </div>
            )}

            {razorpayEnabled && (
              <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="payment-method-picker">
                <button
                  type="button"
                  data-testid="pay-method-online"
                  onClick={() => setPayMethod("online")}
                  className={`rounded-xl border p-4 text-left transition-all duration-300 ${
                    payMethod === "online" ? "border-primary bg-primary/5 shadow-sm" : "border-border bg-white/50 hover:border-primary/40"
                  }`}
                >
                  <CreditCard className="w-5 h-5 text-primary" />
                  <p className="text-sm font-bold mt-2">Pay online</p>
                  <p className="text-xs text-muted-foreground mt-0.5">UPI, cards, netbanking via Razorpay</p>
                </button>
                <button
                  type="button"
                  data-testid="pay-method-cod"
                  onClick={() => setPayMethod("cod")}
                  className={`rounded-xl border p-4 text-left transition-all duration-300 ${
                    payMethod === "cod" ? "border-primary bg-primary/5 shadow-sm" : "border-border bg-white/50 hover:border-primary/40"
                  }`}
                >
                  <Wallet className="w-5 h-5 text-primary" />
                  <p className="text-sm font-bold mt-2">Pay on confirmation / COD</p>
                  <p className="text-xs text-muted-foreground mt-0.5">We call to confirm, pay on delivery</p>
                </button>
              </div>
            )}

            <div className="mt-6 rounded-xl bg-secondary/40 p-5 space-y-1.5">
              {items.map((i) => (
                <div key={i.id} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{i.name}{i.variant ? ` (${i.variant})` : ""}{i.pack ? ` [${i.pack}]` : ""} × {i.qty}</span>
                  <span className="font-medium">{inr(i.price * i.qty)}</span>
                </div>
              ))}
              {discount > 0 && (
                <div className="flex justify-between text-sm pt-2 border-t border-border/60">
                  <span className="text-primary font-semibold">Coupon {coupon?.code}</span>
                  <span data-testid="checkout-discount" className="font-semibold text-primary">−{inr(discount)}</span>
                </div>
              )}
              <div className={`flex justify-between text-sm ${discount > 0 ? "" : "pt-2 border-t border-border/60"}`}>
                <span className="text-muted-foreground">Shipping</span>
                <span className="font-medium">{shipping === 0 ? "Free" : inr(shipping)}</span>
              </div>
              <div className="flex justify-between font-serif text-lg font-semibold">
                <span>Total</span>
                <span data-testid="checkout-total">{inr(grandTotal)}</span>
              </div>
            </div>

            <button
              data-testid="place-order-button"
              type="submit"
              disabled={placing || items.length === 0}
              className="w-full mt-6 rounded-full bg-primary text-primary-foreground py-3.5 text-sm font-bold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
            >
              {placing && <Loader2 className="w-4 h-4 animate-spin" />}
              {placing
                ? "Processing…"
                : payMethod === "online" && razorpayEnabled
                  ? `Pay ${inr(grandTotal)} securely`
                  : `Place order · ${inr(grandTotal)}`}
            </button>
            <p className="text-[11px] text-center text-muted-foreground mt-3">
              {razorpayEnabled ? "Secure payments by Razorpay · COD available" : "Pay on confirmation / Cash on delivery · No advance payment needed"}
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
